/**
 * @fileoverview Pure builder for KorvoChat message debug panel payload.
 * @purpose Keeps `runKorvoChatMessageDebugFlow` thin (fetch + I/O) and unit-testable without network.
 * @usage Imported by `korvo-chat-message-debug.ts` only.
 * @ownership KorvoChat
 * @dependencies `./types`, `@korvo/core/shared/constants/default-models`
 * @restrictions Uses `navigator` / `Intl` (browser); unit tests run under jsdom.
 */

import { DEFAULT_SUMMARY_TITLE_MODEL } from '@korvo/core/shared/constants/default-models';

import type { ChatMessage, ChatSession, KorvoChatConfig } from './types';

export type KorvoChatThreadConfigSnapshot = {
  model?: string
  provider?: string
  temperature?: number
} | null

type MessageDebugExtensions = ChatMessage & {
  createdAt?: string
  updatedAt?: string
  processingTime?: number
  tokensIn?: number
  tokensOut?: number
  costCents?: number
  isQueryMessage?: boolean
  isHelperMessage?: boolean
  ragResults?: unknown[]
}

export type BuildKorvoChatMessageDebugInfoParams = {
  message: MessageDebugExtensions
  currentSession: ChatSession | null
  config: KorvoChatConfig
  threadConfig: KorvoChatThreadConfigSnapshot
  mentorAgentDebug: Record<string, unknown> | null
}

/**
 * Assembles the debug modal payload from message, session, and optional server snapshots.
 */
export function buildKorvoChatMessageDebugInfoData(
  params: BuildKorvoChatMessageDebugInfoParams,
): Record<string, unknown> {
  const { message, currentSession, config, threadConfig, mentorAgentDebug } = params

  const debugInfoData: Record<string, unknown> = {
    messageId: message.id,
    timestamp: message.timestamp,
    role: message.role,
    content: message.content,
    threadId: currentSession?.threadId,
    sessionId: currentSession?.id,
    packageName: config.portal.packageName,
    systemName: config.portal.systemName,
    createdAt: message.createdAt,
    updatedAt: message.updatedAt,
    ...(message.role === 'user' && {
      inputData: {
        characterCount: message.content.length,
        wordCount: message.content.split(/\s+/).length,
        lineCount: message.content.split('\n').length,
        hasFiles: message.files && message.files.length > 0,
        fileCount: message.files?.length || 0,
        files: message.files || [],
        clientMessageId: message.id,
        timestamp: message.timestamp,
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      transmissionData: {
        sentAt: message.timestamp,
        apiEndpoint: config.portal.apiEndpoint,
        method: 'POST',
        contentType: 'application/json',
        threadCreated: !!currentSession?.threadId,
      },
    }),
    ...(message.role === 'assistant' && {
      model: threadConfig?.model || message.metadata?.model || 'openai/gpt-4o-mini',
      provider: threadConfig?.provider || message.metadata?.provider || 'openrouter',
      systemPrompt:
        (mentorAgentDebug as { systemPrompt?: string } | null)?.systemPrompt || config.agent.systemPrompt,
      toolCalls: message.metadata?.toolCalls || [],
      processingTime: message.processingTime ?? undefined,
      tokens:
        message.tokensIn !== undefined || message.tokensOut !== undefined
          ? {
              input: message.tokensIn ?? 0,
              output: message.tokensOut ?? 0,
              total: (message.tokensIn ?? 0) + (message.tokensOut ?? 0),
            }
          : undefined,
      settings: {
        temperature: threadConfig?.temperature ?? config.agent.temperature ?? 0.7,
        maxTokens: config.agent.maxTokens ?? 2000,
        streaming: true,
      },
      costCents: message.costCents ?? undefined,
    }),
    ...((message.role === 'query' || message.isQueryMessage) && {
      agentType: 'query',
      model:
        (message.metadata?.agentDebug as { model?: string } | undefined)?.model || DEFAULT_SUMMARY_TITLE_MODEL,
      systemPrompt:
        (message.metadata?.agentDebug as { systemPrompt?: string } | undefined)?.systemPrompt || 'Query Agent',
      settings: (message.metadata?.agentDebug as { settings?: unknown } | undefined)?.settings || {},
      ragResults: message.ragResults || [],
      output: message.content,
    }),
    ...((message.role === 'helper' || message.isHelperMessage) && {
      agentType: 'helper',
      model:
        (message.metadata?.agentDebug as { model?: string } | undefined)?.model || DEFAULT_SUMMARY_TITLE_MODEL,
      systemPrompt:
        (message.metadata?.agentDebug as { systemPrompt?: string } | undefined)?.systemPrompt || 'Helper Agent',
      settings: (message.metadata?.agentDebug as { settings?: unknown } | undefined)?.settings || {},
      output: message.content,
    }),
    metadata: message.metadata || {},
    files: message.files || [],
    toolCalls: message.toolCalls || [],
    toolResults: message.toolResults || [],
    ...(mentorAgentDebug && {
      agentRunDetails: mentorAgentDebug.agentRunDetails,
      settingsUsed: mentorAgentDebug.settingsUsed,
      ragContext: mentorAgentDebug.ragContext,
      multiAgentDebug: mentorAgentDebug.rawDebug,
    }),
  }

  return debugInfoData
}
